import { useCallback, useEffect, useMemo, useState } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import {
  useHubspotSyncMutation,
  useIsHubspotSyncing,
} from '../../api/client-billing-profile-api-client'
import { extractError } from '../../api/extract-error'
import type { HubspotSyncUpdatedItemDto } from '../../api/dto/client-billing-profile.dto'
import { useFundingInviteAccess } from '../UserManagement/useFundingInviteAccess'
import { useAccessGrantRun } from './useAccessGrantRun'
import {
  buildProfileUpdate,
  dedupeEmails,
  hasPaymentTypeChange,
  hasPlanStartChange,
  paymentTypeLabel,
  planStartLabel,
  submitFailureReason,
} from './hubspotSync.helpers'

interface UseHubspotSyncChangesParams {
  changes: HubspotSyncUpdatedItemDto[]
  /** The profile's recipients as they stand, owned by the run. */
  recipientsByProfileId: Record<number, string[]>
  /** Identifies the run these changes came from; a new one clears old choices. */
  runId: number
  onApplied: (clientBillingProfileId: number, emails: string[]) => void
  onClose: () => void
}

/** One matched profile HubSpot wants to add recipients or a payment type to. */
export interface ChangeRow {
  item: HubspotSyncUpdatedItemDto
  included: boolean
  addedEmails: string[]
  grantedEmails: string[]
  paymentTypeNote: string | null
  planStartNote: string | null
  /** Why the last submit refused this row; null until one does. */
  submitError: string | null
}

const grantKey = (profileId: number, email: string) =>
  `${profileId}:${email.toLowerCase()}`

/**
 * The Changes modal: which matched profiles to bring in line with HubSpot, and
 * whether each newly added address is also granted funding access.
 */
export const useHubspotSyncChanges = ({
  changes,
  recipientsByProfileId,
  runId,
  onApplied,
  onClose,
}: UseHubspotSyncChangesParams) => {
  const [excludedIds, setExcludedIds] = useState<Record<number, boolean>>({})
  const [grantByEmail, setGrantByEmail] = useState<Record<string, boolean>>({})
  const [submitErrors, setSubmitErrors] = useState<Record<number, string>>({})

  useEffect(() => {
    setExcludedIds({})
    setGrantByEmail({})
    setSubmitErrors({})
  }, [runId])

  const inviteAccess = useFundingInviteAccess()
  const syncMutation = useHubspotSyncMutation()
  const isSyncing = useIsHubspotSyncing()
  const accessGrants = useAccessGrantRun(
    'Recipients were updated, but granting access failed'
  )

  const rows = useMemo<ChangeRow[]>(
    () =>
      changes.map((item) => {
        const id = item.clientBillingProfileId
        return {
          item,
          included: !excludedIds[id],
          addedEmails: item.addedEmails,
          grantedEmails: inviteAccess.allowed
            ? item.addedEmails.filter(
                (email) => grantByEmail[grantKey(id, email)] ?? true
              )
            : [],
          paymentTypeNote: hasPaymentTypeChange(item)
            ? `${paymentTypeLabel(item.currentPaymentType)} → ${paymentTypeLabel(item.proposedPaymentType)}`
            : null,
          planStartNote: hasPlanStartChange(item)
            ? `${planStartLabel(item.currentPlanEffectiveDate)} → ${planStartLabel(item.proposedPlanEffectiveDate)}`
            : null,
          submitError: submitErrors[id] ?? null,
        }
      }),
    [changes, excludedIds, grantByEmail, inviteAccess.allowed, submitErrors]
  )

  const includedRows = useMemo(() => rows.filter((row) => row.included), [rows])
  const grantCount = useMemo(
    () => includedRows.reduce((sum, row) => sum + row.grantedEmails.length, 0),
    [includedRows]
  )
  /**
   * Addresses the run could grant, i.e. everything a ticked company is adding.
   * Unticking a company takes its addresses out of the maths entirely, so this
   * and `grantCount` always describe the same set.
   */
  const grantableCount = useMemo(
    () => includedRows.reduce((sum, row) => sum + row.addedEmails.length, 0),
    [includedRows]
  )

  /** The whole intended list, so a later Needs review delete still composes. */
  const targetEmails = useCallback(
    (row: ChangeRow) =>
      dedupeEmails([
        ...(recipientsByProfileId[row.item.clientBillingProfileId] ?? []),
        ...row.addedEmails,
      ]),
    [recipientsByProfileId]
  )

  const startGrants = useCallback(
    (applied: ChangeRow[]) => {
      // The run spans several profiles and an address can be on more than one,
      // so both the request and the report gather the companies per address.
      // One item per person, never per company-email pair: a second item for
      // the same address re-issues the invitation and revokes the first link.
      // Keyed lowercase, which is the address the server reports back.
      const byEmail: Record<
        string,
        { clientPublicKeys: string[]; companies: string[] }
      > = {}
      for (const row of applied) {
        for (const email of row.grantedEmails) {
          const key = email.toLowerCase()
          const entry = byEmail[key] ?? { clientPublicKeys: [], companies: [] }
          entry.clientPublicKeys.push(row.item.clientPublicKey)
          entry.companies.push(row.item.companyName)
          byEmail[key] = entry
        }
      }

      const grants = Object.entries(byEmail).map(
        ([email, { clientPublicKeys }]) => ({
          email,
          personId: null,
          clientPublicKeys,
          roleIds: inviteAccess.roleIds,
        })
      )
      const companiesByEmail = Object.fromEntries(
        Object.entries(byEmail).map(([email, { companies }]) => [
          email,
          companies,
        ])
      )
      accessGrants.grantAccess(
        `Recipients added to ${applied.length} billing ${applied.length === 1 ? 'profile' : 'profiles'}.`,
        grants,
        companiesByEmail
      )
    },
    [accessGrants.grantAccess, inviteAccess.roleIds]
  )

  const handleSubmit = useCallback(() => {
    // A review delete still writing would be overwritten by these lists.
    if (includedRows.length === 0 || isSyncing) return
    const applied = includedRows.map((row) => ({
      row,
      emails: targetEmails(row),
    }))

    syncMutation.mutate(
      {
        updates: applied.map(({ row, emails }) =>
          buildProfileUpdate(row.item, emails, {
            // Only the ticked addresses, so an unticked one is invoiced without
            // being handed access to the company.
            accessEmails: row.grantedEmails,
            paymentType: hasPaymentTypeChange(row.item)
              ? (row.item.proposedPaymentType ?? undefined)
              : undefined,
            planEffectiveDate: hasPlanStartChange(row.item)
              ? (row.item.proposedPlanEffectiveDate ?? undefined)
              : undefined,
          })
        ),
      },
      {
        // A 2xx can still refuse individual profiles. Only the ones that were
        // written are resolved and granted; the rest keep their place on the
        // list with the server's reason, so nothing is lost silently.
        onSuccess: (result) => {
          const failed = result?.failed ?? []
          const written: { row: ChangeRow; emails: string[] }[] = []
          const refused: { row: ChangeRow; error: string }[] = []

          for (const { row, emails } of applied) {
            const error = submitFailureReason(failed, {
              clientBillingProfileId: row.item.clientBillingProfileId,
              clientPublicKey: row.item.clientPublicKey,
            })
            if (isNil(error)) written.push({ row, emails })
            else refused.push({ row, error })
          }

          if (written.length > 0) {
            notification.success({
              message: `${written.length} billing ${written.length === 1 ? 'profile' : 'profiles'} updated`,
            })
          }
          for (const { row, emails } of written) {
            onApplied(row.item.clientBillingProfileId, emails)
          }
          startGrants(written.map(({ row }) => row))

          setSubmitErrors(
            Object.fromEntries(
              refused.map(({ row, error }) => [
                row.item.clientBillingProfileId,
                error,
              ])
            )
          )
          if (refused.length === 0) {
            onClose()
            return
          }
          notification.warning({
            message: `${refused.length} billing ${refused.length === 1 ? 'profile' : 'profiles'} could not be updated`,
          })
        },
        onError: (error) => {
          notification.error({
            message: extractError(error, 'Failed to apply the changes'),
          })
        },
      }
    )
  }, [
    includedRows,
    isSyncing,
    targetEmails,
    syncMutation.mutate,
    onApplied,
    startGrants,
    onClose,
  ])

  return {
    rows,
    accessGrants,
    includedCount: includedRows.length,
    totalCount: rows.length,
    grantCount,
    grantableCount,
    canGrant: inviteAccess.allowed,
    isSubmitting: syncMutation.isPending,
    /** Another panel's sync write is in flight, so Apply waits for it. */
    isBlocked: isSyncing && !syncMutation.isPending,
    setIncluded: (profileId: number, included: boolean) =>
      setExcludedIds((curr) => ({ ...curr, [profileId]: !included })),
    setAllIncluded: (included: boolean) =>
      setExcludedIds(
        included
          ? {}
          : Object.fromEntries(
              changes.map((item) => [item.clientBillingProfileId, true])
            )
      ),
    setGranted: (profileId: number, email: string, granted: boolean) =>
      setGrantByEmail((curr) => ({
        ...curr,
        [grantKey(profileId, email)]: granted,
      })),
    // Only ticked companies, so the control matches the count beside it.
    setAllGranted: (granted: boolean) =>
      setGrantByEmail((curr) => ({
        ...curr,
        ...Object.fromEntries(
          includedRows.flatMap((row) =>
            row.addedEmails.map((email) => [
              grantKey(row.item.clientBillingProfileId, email),
              granted,
            ])
          )
        ),
      })),
    isGranted: (profileId: number, email: string) =>
      grantByEmail[grantKey(profileId, email)] ?? true,
    handleSubmit,
  }
}
